import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronRight, RefreshCw, Sparkles } from "lucide-react";
import { api, formatDate, formatMoney } from "../api";
import { useAuth } from "../auth";
import { useApi } from "../hooks";
import { ChartCard, ChartEmpty, AreaChart, BarList, ColumnChart } from "../components/Charts";
import ConnectionNotice from "../components/ConnectionNotice";
import { EmptyState, ErrorState, MetricCard, PageHeader, PageSkeleton, StatusBadge } from "../components/Ui";
import { AdsRange } from "../components/AdsRange";
import { TILES, TrendControls } from "./Ads";
import { withoutClient } from "../insights";
import {
  callBuckets, changeTone, channelName, comparisonNotes, sortPlatforms, fmtCtr, fmtInt, fmtMoneyWhole, fmtNum1, fmtRoas, jobRevenueEmptyMessage, platformName, section, shortDate, trendPoints,
  type AdsChangeRow, type AdsReport, type Budget, type CallsSection, type Campaign, type ConversionAction, type HealthSection, type RevenueSection, type SummarySection, type TrendMetric, type TrendSection,
} from "../ads";
import { reportRangeQuery, startOfWeek } from "../date-range";

const Unavailable = () => <ChartEmpty>This part could not be loaded right now. Refresh to try again.</ChartEmpty>;
/** A campaign row. An LSA row has no call count (null): its calls ring LSA's own numbers, not a forwarding number. */
type ReportCampaign = Omit<Campaign, "calls"> & { calls: number | null };
/** Google's system action names in words ("local_services_phone_lead" is LSA's own phone lead); anything else as named. */
const SYSTEM_ACTIONS: Record<string, string> = { local_services_phone_lead: "LSA phone leads", local_services_message_lead: "LSA message leads", local_services_booking_lead: "LSA booking leads" };
const actionName = (name: string) => SYSTEM_ACTIONS[name] ?? (/^[a-z0-9]+(_[a-z0-9]+)+$/.test(name) ? (name.charAt(0).toUpperCase() + name.slice(1)).replace(/_/g, " ") : name);
/** A conversion action; counted: false is one Google records but leaves out of its Conversions total. */
type CountedAction = ConversionAction & { counted?: boolean };
// Google names LSA campaigns "LocalServicesCampaign:SystemGenerated:…"; the account's own label says which one it is.
const campaignName = (c: ReportCampaign) => (/^LocalServicesCampaign:SystemGenerated/.test(c.name) && c.account_label ? c.account_label : c.name);

/**
 * The calls chart's bars (callBuckets: days, or weeks from Monday), each marked unsynced when
 * none of its days has been synced yet, so it draws as a gap like Daily trend, not a 0.
 */
function callColumns(daily: { date: string; calls: number; unobserved?: boolean }[]) {
  const buckets = callBuckets(daily);
  if (!daily.some((d) => d.unobserved)) return buckets.map((b) => ({ ...b, unsynced: false }));
  const weekly = buckets.some((b) => b.weekly);
  const keyOf = (day: string) => (weekly ? startOfWeek(day) : day);
  const keys = [...new Set(daily.map((d) => keyOf(d.date)))];
  return buckets.map((b, i) => ({ ...b, unsynced: daily.filter((d) => keyOf(d.date) === keys[i]).every((d) => d.unobserved) }));
}

/** "JK|Sacramento|PMax|Leads|V1" may break after each "|" on a phone, never inside a word. */
const breakAtPipes = (name: string) => name.split("|").flatMap((part, i, all) => (i < all.length - 1 ? [part + "|", <wbr key={i} />] : [part]));

export default function AdsClient() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const admin = user?.role === "superadmin";
  const range = reportRangeQuery(params);
  const { data, loading, refreshing, error, reload } = useApi<AdsReport>(`/api/ads/clients/${id}/report?${range}`);
  // The same request ConnectionNotice makes (cached a minute): whether its box already says Google stopped.
  const feeds = useApi<Record<string, { status?: string }>>(id ? `/api/connections/health?client_id=${id}` : null);
  const googleDown = ["needs_key", "failing", "stale"].includes(feeds.data?.google_ads?.status || "");
  const [metric, setMetric] = useState<TrendMetric>("spend");
  const [platform, setPlatform] = useState("all");
  const [campaignSort, setCampaignSort] = useState<"spend" | "cpa" | "conversions">("spend");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const [whyOpen, setWhyOpen] = useState(false);
  const navigate = useNavigate();
  if (loading) return <PageSkeleton />;
  if (error || !data) return <ErrorState message={error || "No report was returned."} onRetry={reload} />;

  const summary = section<SummarySection>(data, "summary");
  const trend = section<TrendSection>(data, "trend");
  const campaigns = section<ReportCampaign[]>(data, "campaigns");
  const calls = section<CallsSection>(data, "calls");
  const revenue = section<RevenueSection>(data, "revenue");
  const budget = section<Budget | { budget: null }>(data, "budget");
  const health = section<HealthSection>(data, "health");
  const actions = section<CountedAction[]>(data, "conversion_actions");
  const changes = section<AdsChangeRow[]>(data, "changes");
  const daily = trend.data?.daily || [];
  const platforms = sortPlatforms([...new Set(daily.flatMap((d) => Object.keys(d.by_platform)))]);
  const trendPlatform = platforms.includes(platform) ? platform : "all";
  const sortedCampaigns = [...(campaigns.data || [])].sort((a, b) =>
    campaignSort === "cpa" ? (a.cpa ?? Infinity) - (b.cpa ?? Infinity) : b[campaignSort] - a[campaignSort]);
  const b = budget.data && budget.data.budget !== null ? (budget.data as Budget) : null;
  const clientId = data.client.id;
  const rangeText = `${shortDate(data.period.from)} to ${shortDate(data.period.to)}`;
  const detailFrom = data.detail_from ? shortDate(data.detail_from) : null;
  const hasAds = daily.some((d) => Object.keys(d.by_platform).length > 0 || d.spend || d.clicks || d.conversions);
  const covers = revenue.data && revenue.data.available ? revenue.data.covers : null;
  const campaignTotals = sortedCampaigns.reduce((t, c) => ({ spend: t.spend + c.spend, impressions: t.impressions + c.impressions, clicks: t.clicks + c.clicks, conversions: t.conversions + c.conversions, calls: t.calls + (c.calls ?? 0) }), { spend: 0, impressions: 0, clicks: 0, conversions: 0, calls: 0 });
  // A campaign row opens it in Campaigns, in its own account, where it can be paused or its budget changed.
  const campaignLink = (c: ReportCampaign) => `/ads-campaigns?client_id=${clientId}${c.customer_id ? `&account=${c.customer_id}` : ""}`;
  const callChart = callColumns(calls.data?.daily || []);
  const callsWeekly = callChart.some((b) => b.weekly);
  // The actions Google counts (its Conversions tile) first; the ones it only records (a GA4 page view, its own Local actions) after them.
  const countedActions = (actions.data || []).filter((a) => a.counted !== false);
  const uncounted = (actions.data || []).filter((a) => a.counted === false);

  async function run(path: string, body: unknown, done: string) {
    setBusy(true);
    setNotice(null);
    try {
      await api(path, { method: "POST", body: JSON.stringify(body) });
      setNotice({ tone: "success", text: done });
      reload();
    } catch (e) {
      setNotice({ tone: "danger", text: e instanceof Error ? e.message : "That did not work." });
    } finally {
      setBusy(false);
    }
  }
  // Marking an alert reviewed clears every repeat of it (the Health page does the same).
  const markReviewed = (anomalyId: number) => run(`/api/health/insights/${anomalyId}/review`, {}, "Alert marked reviewed.");
  // Re-reads the last 90 days of every linked account from Google Ads (read only on Google's side).
  const reread = () => run(`/api/googleads/sync/${clientId}`, { days: 90 }, "The last 90 days were read again from Google Ads.");

  // No account and nothing recorded: one clear line, not a page of $0 tiles and empty cards,
  // and no dates, refresh, re-read or AI for an account that is not there.
  const noAccount = data.client.has_account === false && !hasAds;
  const header = (
    <PageHeader
      title="Ads report"
      description={noAccount ? undefined : `${rangeText}${data.client.status !== "active" ? `, ${data.client.status}` : ""}`}
      actions={noAccount ? undefined : <>
        <AdsRange />
        <button className="button secondary ads-refresh" disabled={refreshing} onClick={reload} aria-label="Refresh"><RefreshCw className={refreshing ? "spin" : ""} aria-hidden="true" /><span>{refreshing ? "Refreshing…" : "Refresh"}</span></button>
        {/* Refresh reloads this page; this one reads Google Ads itself again, so it says so. */}
        {admin && <button type="button" className="button secondary" disabled={busy} onClick={() => void reread()} title="Read the last 90 days of every linked account from Google Ads again">Re-read 90 days</button>}
        <Link className="button secondary" to={`/ads-ai?client_id=${clientId}&ask=${encodeURIComponent("Review this account and propose the 3 changes that matter most")}`}><Sparkles aria-hidden="true" />Analyze with AI</Link>
      </>}
    />
  );

  if (noAccount) {
    return (
      <>
        {header}
        <EmptyState
          title="No Google Ads account linked"
          description={admin ? `Link ${data.client.name}'s Google Ads account to see spend, campaigns and calls here.` : "An administrator links accounts in Setup → Connections."}
          action={admin ? <Link className="button secondary" to={`/client-settings?client_id=${clientId}`}>Open Connections</Link> : undefined}
        />
      </>
    );
  }

  return (
    <>
      {header}
      <ConnectionNotice clientId={clientId} feeds={["google_ads"]} />
      {notice && <p className={`inline-notice ${notice.tone}`} role={notice.tone === "danger" ? "alert" : "status"}><span>{notice.text}</span><button type="button" aria-label="Dismiss" onClick={() => setNotice(null)}>×</button></p>}

      {summary.unavailable ? <Unavailable /> : summary.data && (
        <>
          <section className="metric-grid ads-metrics" aria-label="Totals">
            {TILES.map((tile) => (
              <MetricCard key={tile.key} label={tile.label}
                value={tile.key === "spend" ? fmtMoneyWhole(summary.data!.metrics.spend) : tile.format(summary.data!.metrics[tile.key])}
                change={summary.data!.change[tile.key] ?? null} tone={changeTone(tile.key, summary.data!.change[tile.key])}
                detail={tile.key === "clicks" ? `CTR ${fmtCtr(summary.data!.metrics.ctr)}`
                  : tile.key === "roas" && covers ? `Jobs ${shortDate(covers.from)} to ${shortDate(covers.to)}`
                  : tile.key === "roas" && revenue.data && !revenue.data.available && revenue.data.job_export ? `Job data ends ${shortDate(revenue.data.job_export.last_day)}`
                  : tile.key === "roas" && revenue.data && !revenue.data.available ? "No job data yet"
                  : tile.key === "calls" && detailFrom ? `Since ${detailFrom}` : undefined} />
            ))}
          </section>
          <p className="ads-note">
            {summary.data.previous
              ? `Compared with ${shortDate(data.period.previous.from)} to ${shortDate(data.period.previous.to)}${!summary.data.lsa_split_from && summary.data.compared_platforms.length < platforms.length ? `, for ${summary.data.compared_platforms.map(platformName).join(" and ")} only` : ""}.`
              : "This account's data does not cover the whole earlier period, so there is no comparison."}
            {/* The caveats behind a comparison are one click away, not two lines under every report. */}
            {summary.data.previous && comparisonNotes(summary.data.lsa_split_from, summary.data.calls_from, shortDate).trim() && (
              <> <button type="button" className="text-button ads-why" aria-expanded={whyOpen} onClick={() => setWhyOpen(!whyOpen)}>{whyOpen ? "Hide notes" : "Notes"}</button>
                {whyOpen && <span className="ads-why-text"> {comparisonNotes(summary.data.lsa_split_from, summary.data.calls_from, shortDate)}</span>}</>
            )}
          </p>
        </>
      )}

      <div className="dashboard-charts ads-report-charts">
        <ChartCard className="chart-wide" eyebrow="Ads" title="Daily trend" meta={<TrendControls metric={metric} setMetric={setMetric} platform={trendPlatform} setPlatform={setPlatform} platforms={platforms} />}>
          {trend.unavailable ? <Unavailable /> : hasAds ? (
            <AreaChart integer={metric !== "spend"} data={trendPoints(daily, metric, trendPlatform).map((p) => ({ label: shortDate(p.date), value: p.value }))}
              format={metric === "spend" ? (v) => formatMoney(v, true) : fmtInt} caption={`${metric} per day`} />
          ) : <ChartEmpty>No ads data in this range.</ChartEmpty>}
          {!trend.unavailable && daily.some((d) => d.unobserved) && <p className="ads-note">Gaps are days that have not been synced yet.</p>}
        </ChartCard>

        {/* The total is what Google counted, the same as the Conversions tile; an action it only
            records (Google's own setting per action, stored by the sync) is listed after, marked,
            and left out of the total. */}
        <ChartCard eyebrow="Conversions" title="What counted as a conversion" meta={actions.data && actions.data.length ? `${fmtNum1(countedActions.reduce((a, x) => a + x.conversions, 0))} counted` : undefined}>
          {actions.unavailable ? <Unavailable /> : actions.data && actions.data.length ? (
            <BarList wrap limit={8} data={[...countedActions, ...uncounted].map((a) => ({ label: a.counted === false ? `${actionName(a.name)} (not counted)` : actionName(a.name), value: a.conversions }))} format={(v) => fmtNum1(v)} caption="Conversions by conversion action" />
          ) : <ChartEmpty>No conversions by action in this range.</ChartEmpty>}
          {actions.data && actions.data.length > 0 && <p className="ads-note">Google's own conversion actions.{uncounted.length > 0 && " Not counted means Google records it but leaves it out of its Conversions total."} A phone click or a page visit is not the same as a booked job.</p>}
        </ChartCard>

        <ChartCard eyebrow="Revenue" title="Revenue and ROAS" className="ads-revenue-card">
          {revenue.unavailable ? <Unavailable /> : revenue.data && revenue.data.available ? (
            <>
              {/* The figure first, then one note under it; with no campaign match the note says so too,
                  instead of a centred empty line floating under a small paragraph. */}
              <div className="ads-revenue-stat">
                <strong>{formatMoney(revenue.data.revenue, true)}</strong>
                <span>revenue, ROAS {fmtRoas(revenue.data.roas)}</span>
              </div>
              <p className="ads-note">
                {revenue.data.source === "jobs" ? `${revenue.data.jobs} jobs booked${covers ? ` ${shortDate(covers.from)} to ${shortDate(covers.to)}` : ""}` : `${revenue.data.won_deals} GHL deals marked won`}
                {covers ? ". ROAS uses the spend on the days the job export covers." : "."}
                {!revenue.data.by_campaign.length && (revenue.data.source === "ghl_won"
                  ? <> Won deals by campaign are on the <Link to={`/tracking?client_id=${clientId}`}>Tracking page</Link>.</>
                  : " No job is matched to a campaign yet.")}
              </p>
              {revenue.data.by_campaign.length > 0 && (
                <BarList data={revenue.data.by_campaign.map((c) => ({ label: `${c.campaign} (${c.booked_jobs} jobs)`, value: c.revenue }))} format={(v) => formatMoney(v, true)} caption="Matched revenue by campaign" />
              )}
            </>
          ) : revenue.data && !revenue.data.available && revenue.data.job_export ? (
            <ChartEmpty>{jobRevenueEmptyMessage(revenue.data.job_export, data.period)} <Link to={`/tracking?client_id=${clientId}&tab=setup#conversion-upload`}>Upload a job export</Link></ChartEmpty>
          ) : <ChartEmpty>No job data yet. Upload a job export on the client's <Link to={`/tracking?client_id=${clientId}&tab=setup#conversion-upload`}>Tracking page</Link>.</ChartEmpty>}
        </ChartCard>

        <ChartCard className="chart-wide" eyebrow="Calls" title={callsWeekly ? "Calls from ads per week" : "Calls from ads per day"} meta={calls.data ? `${calls.data.total} ${calls.data.total === 1 ? "call" : "calls"}` : undefined}>
          {calls.unavailable ? <Unavailable /> : calls.data && calls.data.total > 0
            // A bar Google has not synced is a gap with no label: ColumnChart draws a null value
            // as an empty column and its table says "Not available" (its Point type says number).
            ? <ColumnChart data={callChart.map((b) => ({ label: b.label, value: (b.unsynced ? null : b.value) as number }))} format={(v) => (v == null ? "" : String(v))} caption={callsWeekly ? "Calls from ads per week, weeks starting Monday" : "Calls from ads per day"} />
            : <ChartEmpty>No calls from Google forwarding numbers in this range.</ChartEmpty>}
          {calls.data && <p className="ads-note">From Google forwarding numbers{detailFrom ? `, recorded from ${detailFrom}` : ""}. Calls per campaign are in the table below.{calls.data.ctm && calls.data.ctm.total > 0 ? ` ${calls.data.ctm.total} CallTrackingMetrics ${calls.data.ctm.total === 1 ? "call" : "calls"} in this range.` : ""}{callChart.some((b) => b.unsynced) && ` Gaps are ${callsWeekly ? "weeks" : "days"} not synced yet.`}</p>}
        </ChartCard>
      </div>

      <section className="panel">
        <div className="panel-heading">
          <div><h2>Campaigns</h2>{detailFrom && campaigns.data && campaigns.data.length > 0 && <p className="panel-note">{detailFrom} to {shortDate(data.period.to)} only: campaign detail starts {detailFrom}.</p>}</div>
          <div className="segmented-control compact" aria-label="Sort campaigns">
            {(["spend", "conversions", "cpa"] as const).map((k) => <button key={k} type="button" className={campaignSort === k ? "active" : ""} aria-pressed={campaignSort === k} onClick={() => setCampaignSort(k)}>{k === "cpa" ? "Lowest CPA" : k === "spend" ? "Spend" : "Conversions"}</button>)}
          </div>
        </div>
        {campaigns.unavailable ? <Unavailable /> : sortedCampaigns.length ? (
          <div className="table-wrap">
            <table className="ads-table ads-campaigns clickable">
              <thead><tr><th>Campaign</th><th className="ads-col-extra">Type</th><th className="num">Spend</th><th className="num ads-col-extra">Impr.</th><th className="num ads-col-extra">Clicks</th><th className="num ads-col-extra">CTR</th><th className="num ads-col-extra">CPC</th><th className="num">Conv.</th><th className="num">CPA</th><th className="num ads-col-extra">Calls</th></tr></thead>
              <tbody>{sortedCampaigns.map((c) => (
                <tr key={c.campaign_id} onClick={() => navigate(campaignLink(c))}>
                  <td><Link className="ads-campaign-name" to={campaignLink(c)} title={`${campaignName(c)}: open in Campaigns`} onClick={(e) => e.stopPropagation()}>{breakAtPipes(campaignName(c))}</Link></td>
                  <td className="ads-col-extra" data-label="Type">{channelName(c.channel)}</td>
                  <td className="number-cell num" data-label="Spend">{fmtMoneyWhole(c.spend)}</td>
                  <td className="number-cell num ads-col-extra" data-label="Impr.">{fmtInt(c.impressions)}</td>
                  <td className="number-cell num ads-col-extra" data-label="Clicks">{fmtInt(c.clicks)}</td>
                  <td className="number-cell num ads-col-extra" data-label="CTR">{fmtCtr(c.ctr)}</td>
                  <td className="number-cell num ads-col-extra" data-label="CPC">{formatMoney(c.cpc)}</td>
                  <td className="number-cell num" data-label="Conv.">{fmtNum1(c.conversions)}</td>
                  <td className="number-cell num" data-label="CPA">{formatMoney(c.cpa)}</td>
                  <td className="number-cell num ads-col-extra" data-label="Calls">{c.calls == null ? <span title="LSA calls ring LSA's own numbers, so they are not counted here. They are on Calls & coaching.">—</span> : fmtInt(c.calls)}<ChevronRight className="ads-row-go" aria-hidden="true" /></td>
                </tr>
              ))}</tbody>
              {sortedCampaigns.length > 1 && (
                <tfoot><tr>
                  <td><strong>Total</strong></td>
                  <td className="ads-col-extra" />
                  <td className="number-cell num">{fmtMoneyWhole(campaignTotals.spend)}</td>
                  <td className="number-cell num ads-col-extra">{fmtInt(campaignTotals.impressions)}</td>
                  <td className="number-cell num ads-col-extra">{fmtInt(campaignTotals.clicks)}</td>
                  <td className="number-cell num ads-col-extra">{fmtCtr(campaignTotals.impressions ? campaignTotals.clicks / campaignTotals.impressions : null)}</td>
                  <td className="number-cell num ads-col-extra">{formatMoney(campaignTotals.clicks ? campaignTotals.spend / campaignTotals.clicks : null)}</td>
                  <td className="number-cell num">{fmtNum1(campaignTotals.conversions)}</td>
                  <td className="number-cell num">{formatMoney(campaignTotals.conversions ? campaignTotals.spend / campaignTotals.conversions : null)}</td>
                  <td className="number-cell num ads-col-extra">{fmtInt(campaignTotals.calls)}</td>
                </tr></tfoot>
              )}
            </table>
          </div>
        ) : <EmptyState title="No campaign detail in this range" description={`Campaign detail starts ${detailFrom || shortDate("2026-09-21")} and comes from the nightly Google Ads sync.`} />}
      </section>

      {(budget.data || health.data || budget.unavailable || health.unavailable) && (
        <section className="ads-team-only" aria-label="Team only">
          <div className="ads-team-only-head">
            <h2>Team only</h2>
            <Link className="text-button" to={`/ads-campaigns?client_id=${clientId}`}>Manage campaigns</Link>
          </div>
          <div className="dashboard-charts">
            <ChartCard eyebrow="Budget" title="This month">
              {budget.unavailable ? <Unavailable /> : b ? (
                <>
                  <BarList max={Math.max(b.budget, b.projected || 0)} data={[{ label: "Spent so far", value: b.month_spend }, ...(b.projected ? [{ label: "Projected month end", value: b.projected }] : []), { label: "Monthly budget", value: b.budget }]} format={(v) => formatMoney(v, true)} caption="Spend this month against budget" />
                  <p className="ads-note">{b.used_pct ?? 0}% of budget used since {shortDate(b.month_start)}.</p>
                </>
              ) : <ChartEmpty>No monthly ad budget is set on this client. Add it on the <Link to={`/clients/${clientId}`}>client profile</Link>.</ChartEmpty>}
            </ChartCard>
            <ChartCard eyebrow="Health" title="Alerts" meta={health.data?.last_synced_at ? `Synced ${formatDate(health.data.last_synced_at)}` : "Never synced"}>
              {health.unavailable ? <Unavailable /> : health.data && (
                <>
                  {/* The connection box at the top already says Google stopped: not again here. */}
                  {health.data.stale && !googleDown && <p className="ads-unavailable">Stale: no successful Google Ads sync in two days, or no data in a recent range.</p>}
                  {health.data.anomalies.length ? health.data.anomalies.map((a) => (
                    <div className="ads-anomaly" key={a.id}>
                      <div className="ads-anomaly-head">
                        <StatusBadge tone={a.severity === "high" || a.severity === "critical" ? "danger" : "warning"}>{a.severity}</StatusBadge>
                        <strong>{withoutClient(a.title, data.client.name)}</strong>
                        {a.repeats && a.repeats > 1 ? <span className="muted">Flagged on {a.repeats} {a.repeats === 1 ? "day" : "days"}</span> : null}
                        {admin && <button type="button" className="text-button" disabled={busy} onClick={() => void markReviewed(a.id)}>Mark reviewed</button>}
                      </div>
                      <p>{a.body}</p>
                      {a.recommendation && <p>First check: {a.recommendation}</p>}
                    </div>
                  )) : <ChartEmpty>{health.data.stale || googleDown ? "No other alerts in the last 14 days." : "No open alerts in the last 14 days."}</ChartEmpty>}
                </>
              )}
            </ChartCard>
            {changes.data && changes.data.length > 0 && (
              <ChartCard className="chart-wide ads-changes-card" eyebrow="Changes" title="Changes made in NOLA in this range" meta={<Link to={`/ads-campaigns?client_id=${clientId}`}>Open Campaigns</Link>}>
                <ul className="ads-changes">
                  {changes.data.map((c) => (
                    <li key={c.id}><span className="ads-changes-day">{shortDate(c.day)}</span><span>{c.title}{c.status === "undone" ? " (undone)" : ""}</span>{c.by_name && <span className="muted">{c.by_name}</span>}</li>
                  ))}
                </ul>
              </ChartCard>
            )}
          </div>
        </section>
      )}
    </>
  );
}
